/**
 * Fantasy Console 0 - Editor Settings Component
 *
 * Modal for configuring code editor preferences.
 */

import { useState, useEffect } from 'react'
import {
  CodeEditorSettings,
  loadPreferences,
  savePreferences,
  DEFAULT_CODE_EDITOR_SETTINGS,
} from '../../utils/storage'
import { CloseIcon } from '../shared/icons'
import styles from './EditorSettings.module.css'
import modalStyles from '../../styles/modal.module.css'

interface EditorSettingsProps {
  onClose: () => void
  onSettingsChange: (settings: CodeEditorSettings) => void
}

export function EditorSettings({
  onClose,
  onSettingsChange,
}: EditorSettingsProps) {
  const [settings, setSettings] = useState<CodeEditorSettings>(
    DEFAULT_CODE_EDITOR_SETTINGS,
  )

  useEffect(() => {
    const prefs = loadPreferences()
    setSettings(prefs.codeEditor || DEFAULT_CODE_EDITOR_SETTINGS)
  }, [])

  const handleChange = <K extends keyof CodeEditorSettings>(
    key: K,
    value: CodeEditorSettings[K],
  ) => {
    const newSettings = { ...settings, [key]: value }
    setSettings(newSettings)
  }

  const handleSave = () => {
    savePreferences({ codeEditor: settings })
    onSettingsChange(settings)
    window.dispatchEvent(new CustomEvent('editor-settings-changed'))
    onClose()
  }

  const handleReset = () => {
    setSettings(DEFAULT_CODE_EDITOR_SETTINGS)
  }

  return (
    <div className={modalStyles.modalOverlay} onClick={onClose}>
      <div
        className={`${modalStyles.modalDialog} ${styles.modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.modalHeader}>
          <h3 className={modalStyles.modalTitle}>Editor Settings</h3>
          <button className={modalStyles.modalCloseButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={`${modalStyles.modalContent} ${styles.content}`}>
          {/* Font Size */}
          <div className={styles.setting}>
            <label className={styles.label}>Font Size</label>
            <select
              className={styles.select}
              value={settings.fontSize}
              onChange={(e) =>
                handleChange(
                  'fontSize',
                  Number(e.target.value) as CodeEditorSettings['fontSize'],
                )
              }
            >
              <option value={12}>12px</option>
              <option value={13}>13px (default)</option>
              <option value={14}>14px</option>
              <option value={16}>16px</option>
              <option value={18}>18px</option>
            </select>
          </div>

          {/* Tab Size */}
          <div className={styles.setting}>
            <label className={styles.label}>Tab Size</label>
            <select
              className={styles.select}
              value={settings.tabSize}
              onChange={(e) =>
                handleChange(
                  'tabSize',
                  Number(e.target.value) as CodeEditorSettings['tabSize'],
                )
              }
            >
              <option value={2}>2 spaces (default)</option>
              <option value={4}>4 spaces</option>
            </select>
          </div>

          {/* Line Wrap */}
          <div className={styles.setting}>
            <label className={styles.label}>
              <input
                type='checkbox'
                checked={settings.lineWrap}
                onChange={(e) => handleChange('lineWrap', e.target.checked)}
              />
              <span>Line Wrap</span>
            </label>
            <p className={styles.hint}>
              Wrap long lines instead of horizontal scroll
            </p>
          </div>

          {/* Auto Run */}
          <div className={styles.setting}>
            <label className={styles.label}>
              <input
                type='checkbox'
                checked={settings.autoRun}
                onChange={(e) => handleChange('autoRun', e.target.checked)}
              />
              <span>Auto Run</span>
            </label>
            <p className={styles.hint}>
              Automatically run code after changes (with debounce)
            </p>
          </div>

          {/* Auto Run Debounce */}
          {settings.autoRun && (
            <div className={styles.setting}>
              <label className={styles.label}>Auto Run Delay</label>
              <select
                className={styles.select}
                value={settings.autoRunDebounce}
                onChange={(e) =>
                  handleChange('autoRunDebounce', Number(e.target.value))
                }
              >
                <option value={300}>300ms (fast)</option>
                <option value={500}>500ms (default)</option>
                <option value={1000}>1000ms (slow)</option>
                <option value={2000}>2000ms (very slow)</option>
              </select>
            </div>
          )}

          {/* Vim Mode */}
          <div className={styles.setting}>
            <label className={styles.label}>
              <input
                type='checkbox'
                checked={settings.vimMode}
                onChange={(e) => handleChange('vimMode', e.target.checked)}
              />
              <span>Vim Mode</span>
            </label>
            <p className={styles.hint}>Enable Vim keybindings in the editor</p>
          </div>
        </div>

        <div className={`${modalStyles.modalFooter} ${styles.footer}`}>
          <button className='btn btn-ghost' onClick={handleReset}>
            Reset to Defaults
          </button>
          <div className={modalStyles.modalActions}>
            <button className='btn btn-secondary' onClick={onClose}>
              Cancel
            </button>
            <button className='btn btn-primary' onClick={handleSave}>
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function loadCodeEditorSettings(): CodeEditorSettings {
  const prefs = loadPreferences()
  return prefs.codeEditor || DEFAULT_CODE_EDITOR_SETTINGS
}
